// ── Ambient company: the one line under a show's title ───────────────────────
// Pure. Takes the row show_company() returns for one show and says, in the app's
// voice, who else is here. Null when there is nobody, so an empty app never looks
// empty by announcing zero. "near" is people within a season of you (null when you
// have no position yet); it only speaks when it adds something to "watching".
export function companyLine(row) {
  if (!row) return null
  const w = Math.max(0, row.watching | 0)
  const m = Math.max(0, row.marked_week | 0)
  const n = row.near == null ? null : Math.max(0, row.near | 0)
  const parts = []
  if (w > 0) parts.push(`${w} ${w === 1 ? 'other' : 'others'} watching`)
  if (n != null && n > 0 && n < w) parts.push(`${n} near you`)
  if (m > 0) parts.push(`${m} marked this week`)
  if (!parts.length) return null
  return parts.join(' · ')
}

// A short label for the door's row count, same rules.
export function companionsTitle(count) {
  if (!count) return 'Nobody near you yet'
  return count === 1 ? '1 person is near you on this' : `${count} people are near you on this`
}

// Why this person is a near match, in one short line.
export function matchReason(m) {
  if (!m) return ''
  const shows = Math.max(0, m.shared_shows | 0)
  const names = Array.isArray(m.genre_names) ? m.genre_names.filter(Boolean).slice(0, 2) : []
  const parts = []
  if (shows > 0) parts.push(`${shows} show${shows === 1 ? '' : 's'} in common${m.shared_show_name ? `, incl. ${m.shared_show_name}` : ''}`)
  if (names.length) parts.push(`into ${names.join(' & ')}`)
  return parts.join(' · ')
}

// The named line: "Melissa is on S4·E3, one ahead of you". Pure, so it is testable.
// Gap is in episodes within a season when seasons match, else in seasons; never
// pretends to know episode counts across seasons.
const words = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const n2w = (n) => words[n] || String(n)
// { name, rest }: the renderer bolds the name and prints the rest. It used to recover
// the name by splitting the finished line on ' is ', which "Jason finished this" does
// not contain, so the slice fell back to the last character and the card read
// "Jason finished thiss" (iPhone simulator, 2026-10-22). Structured, it cannot drift.
export function friendLineParts(me, f) {
  if (!f) return null
  const name = f.name_private ? f.username : (f.display_name || f.username || 'Someone')
  const r = (rest) => ({ name, rest })
  if (f.status === 'completed') return r(' finished this')
  if (f.season == null) return r(' is on this too')
  const where = `S${f.season}·E${f.episode ?? 1}`
  if (!me?.season) return r(` is on ${where}`)
  if (f.season === me.season) {
    const d = (f.episode ?? 1) - (me.episode ?? 1)
    if (d === 0) return r(` is right where you are, ${where}`)
    return r(` is on ${where}, ${n2w(Math.abs(d))} ${d > 0 ? 'ahead of' : 'behind'} you`)
  }
  const ds = f.season - me.season
  return r(` is on ${where}, ${n2w(Math.abs(ds))} season${Math.abs(ds) === 1 ? '' : 's'} ${ds > 0 ? 'ahead of' : 'behind'} you`)
}
export function friendLine(me, f) {
  const p = friendLineParts(me, f)
  return p ? p.name + p.rest : null
}
